import { UploadOutlined } from '@ant-design/icons';
import { Button, Upload, UploadFile, UploadProps } from 'antd';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { GrispiInputProps } from '..';
import useFileSizeCompare from '../../../hooks/useFileSizeCompare';
import { useCurrentUserStore } from '../../../store/RootStoreContext';
import { grispiNotification } from '../../../store/useNotificationStore';
import GrispiInputContainer from '../GrispiInputContainer';

type GrispiAttachmentInputProps = Omit<GrispiInputProps<'ATTACHMENTS'>, 'type' | 'options' | 'isNewValueAllowed'>;

const GrispiAttachmentInput = ({
    antdProps,
    buttonText,
    containerProps,
    disabled,
    onChange,
    value
}: GrispiAttachmentInputProps) => {
    const fileList = (value as UploadFile<any>[]) ?? [];
    const { t } = useTranslation('forms');

    const { role } = useCurrentUserStore() ?? {};
    const { compareFn, fileSizeLimit } = useFileSizeCompare(role);

    const handleBeforeUpload: UploadProps['beforeUpload'] = async (file) => {
        const fileSize = file.size / 1024 / 1024;

        if (compareFn(fileSize)) {
            grispiNotification('error', t('attachment_big', { maxSize: fileSizeLimit }), 'error');
            return false || Upload.LIST_IGNORE;
        }

        const newFileList = [...fileList, file];
        onChange(newFileList);
        return file;
    };

    const handleOnPreview: UploadProps['onPreview'] = useCallback(async (file) => {
        let src = file.url;
        if (!src) {
            src = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.readAsDataURL(file.originFileObj);
                reader.onload = () => resolve(reader.result as any);
            });
        }
        const image = new Image();
        image.src = src;
        const imgWindow = window.open(src);
        imgWindow.document.write(image.outerHTML);
    }, []);

    const handleOnRemove: UploadProps['onRemove'] = async (file) => {
        const fileId = file.uid;
        const newFileList = (value as UploadFile[]).filter((file) => file.uid !== fileId);
        onChange(newFileList);
    };

    return (
        <>
            <GrispiInputContainer {...containerProps}>
                <Upload
                    multiple
                    name="file"
                    listType="picture"
                    beforeUpload={handleBeforeUpload}
                    disabled={disabled}
                    onPreview={handleOnPreview}
                    onRemove={handleOnRemove}
                    onChange={(e) => {
                        if (e.file.status === 'done') {
                            const newFileList = [
                                ...fileList.filter((file) => file.uid !== e.file.uid),
                                e.file as UploadFile<any>
                            ];
                            onChange(newFileList);
                        }
                    }}
                    // fileList={fileList}
                    fileList={value as UploadFile<any>[]}
                    customRequest={({ onSuccess }) =>
                        setTimeout(() => {
                            onSuccess('ok', null);
                        }, 0)
                    }
                    {...antdProps}
                >
                    <Button icon={<UploadOutlined />}>{buttonText ?? t('form.uploadButton')}</Button>
                </Upload>
            </GrispiInputContainer>
        </>
    );
};

export default observer(GrispiAttachmentInput);
